import { useState, useEffect, useRef } from "react";
import { Phone, Menu, X, ChevronDown, Star, ArrowRight, BadgeDollarSign, Truck, Timer } from "lucide-react";
import logoAsset from "@/assets/oz-waste-logo.webp.asset.json";
import heroTruck1200 from "@/assets/hero-ute-1200.webp.asset.json";
import heroTruck800 from "@/assets/hero-ute-800.webp.asset.json";
const logo = logoAsset.url;
const heroTruck = heroTruck1200.url;
const heroTruckSrcSet = `${heroTruck800.url} 800w, ${heroTruck1200.url} 1200w`;

import { googleReviewSummary, googleBusinessUrl } from "@/data/reviews";

// Official-style multicolor Google "G" logo (matches SocialProofStats usage).
const GoogleGIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const trustIndicators = [
  { label: "Upfront Quotes", Icon: BadgeDollarSign },
  { label: "We Do the Heavy Lifting", Icon: Truck },
  { label: "Fast Pickup Availability", Icon: Timer },
];


import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

// Priority order: items hide first as screen shrinks (What We Take → Services → How It Works)
const navLinks = [
  { label: "How It Works", href: "/#how-it-works", showClass: "lg:inline-flex", hideClass: "lg:hidden" },
  { label: "Services", href: "/#services", showClass: "xl:inline-flex", hideClass: "xl:hidden" },
  { label: "What We Take", href: "/#what-we-take", showClass: "2xl:inline-flex", hideClass: "2xl:hidden" },
  { label: "Get a Quote", href: "/#quote", showClass: "lg:inline-flex", hideClass: "lg:hidden" },
];


const HeroSection = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dividerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const divider = dividerRef.current;
      const header = headerRef.current;
      if (!divider || !header) return;
      // Trigger when the black feature bar (TrustStrip, which begins at the
      // bottom edge of the diagonal divider) reaches the bottom of the
      // sticky header. Keeps the header yellow throughout the hero.
      const trigger = divider.getBoundingClientRect().bottom <= header.offsetHeight;
      setScrolled((prev) => (prev !== trigger ? trigger : prev));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const headerBg = scrolled ? "bg-white shadow-md" : "bg-[#FDC00E]";
  const drawerBg = scrolled ? "bg-white" : "bg-[#FDC00E]";

  return (
    <>
      {/* Sticky Header, lifted out of the hero section so it stays sticky
          across the whole page. Blends into the hero while at the top,
          transitions to white once the black feature bar reaches it. */}
      <header
        ref={headerRef}
        className={`sticky top-0 z-50 transition-[background-color,box-shadow] duration-300 ease-out ${headerBg}`}
      >
        <div className="container flex flex-nowrap items-center justify-between gap-4 py-3 whitespace-nowrap">
          {/* LEFT: Hamburger + Logo */}
          <div className="flex flex-nowrap items-center gap-3 min-w-0">
            <button
              className="text-[#1a1a1a] p-1 md:hidden"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <a href="/">
              <img src={logo} alt="OZ WASTE CO. Logo" className="h-16 md:h-24 w-auto shrink-0" width="288" height="192" fetchPriority="high" decoding="async" />
            </a>
            {/* Desktop nav links */}
            <nav className="hidden md:flex flex-nowrap items-center gap-6 ml-6">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className={`hidden ${link.showClass} text-[#1a1a1a]/80 font-semibold text-sm hover:text-[#1a1a1a] transition-colors whitespace-nowrap`}
                >
                  {link.label}
                </a>
              ))}
              {/* "More" dropdown surfaces nav items hidden at the current breakpoint */}
              <DropdownMenu>
                <DropdownMenuTrigger
                  aria-label="More navigation"
                  className="hidden md:inline-flex 2xl:hidden items-center gap-1 text-[#1a1a1a]/80 font-semibold text-sm hover:text-[#1a1a1a] transition-colors whitespace-nowrap focus:outline-none"
                >
                  More <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className={`border-[#1a1a1a]/10 ${drawerBg}`}>
                  {navLinks
                    .filter((l) => l.label !== "Get a Quote")
                    .map((link) => (
                      <DropdownMenuItem
                        key={link.label}
                        asChild
                        className={`${link.hideClass} text-[#1a1a1a] font-semibold focus:bg-[#1a1a1a]/10 focus:text-[#1a1a1a]`}
                      >
                        <a href={link.href}>{link.label}</a>
                      </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>
          </div>

          {/* RIGHT: Phone → Get Quote → Book Now */}
          <div className="flex flex-nowrap items-center gap-2 md:gap-3 shrink-0">
            <a
              href="tel:1800556654"
              data-call-source="header_desktop"
              aria-label="Call 1800 556 654"
              className="inline-flex items-center gap-2 border-2 border-[#1a1a1a] text-[#1a1a1a] px-3 md:px-4 py-2 font-bold text-sm rounded-full hover:bg-orange/10 hover:border-orange hover:text-orange-deep transition-colors whitespace-nowrap shrink-0"
            >
              <Phone className="w-4 h-4 shrink-0" />
              <span className="hidden lg:inline">1800 556 654</span>
            </a>
            <a
              href="/#quote"
              className="hidden md:inline-flex border-2 border-[#1a1a1a] text-[#1a1a1a] px-4 py-2 font-bold text-sm rounded-full hover:bg-orange/10 hover:border-orange hover:text-orange-deep transition-colors whitespace-nowrap shrink-0"
            >
              Get Quote
            </a>
            <a
              href="/#quote"
              className="inline-flex items-center gap-1.5 bg-orange text-white px-4 md:px-5 py-2 font-bold text-sm rounded-full hover:bg-orange-deep transition-colors whitespace-nowrap shrink-0"
            >
              Book a Pickup

              <ArrowRight className="w-4 h-4 shrink-0" />
            </a>
          </div>
        </div>

        {/* Mobile nav drawer */}
        {menuOpen && (
          <nav className={`md:hidden border-t border-[#1a1a1a]/10 ${drawerBg}`}>
            <div className="container py-4 flex flex-col gap-3">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  className="text-[#1a1a1a] font-bold text-base py-2 border-b border-[#1a1a1a]/10 last:border-b-0"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="tel:1800556654"
                data-call-source="header_mobile_menu"
                className="flex items-center gap-2 text-[#1a1a1a] font-bold text-base py-2"
              >
                <Phone className="w-5 h-5" />
                1800 556 654
              </a>
            </div>
          </nav>
        )}
      </header>

      <section className="bg-[#FDC00E] relative overflow-hidden">
        {/* Orange energy glow radiating from behind the vehicle (right side) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(circle_at_86%_66%,rgba(246,74,1,0.34),transparent_58%)]"
        />
        {/* Subtle OZ WASTE background graphics, oversized wordmark + slanted shape */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 overflow-hidden hidden md:block">
          <span className="absolute right-[2%] top-1/2 -translate-y-1/2 font-black tracking-tighter text-[#1a1a1a] opacity-[0.06] text-[9rem] lg:text-[12rem] xl:text-[14rem] leading-none select-none">
            WASTE
          </span>
          <span className="absolute right-[46%] bottom-[26%] w-[110px] h-[10px] bg-[#1a1a1a] opacity-[0.07] -skew-x-[28deg]" />
        </div>
        {/* Hero Content */}
        <div className="relative z-10 container">
          <div className="flex flex-col lg:flex-row lg:items-end min-h-[340px] md:min-h-[440px] lg:min-h-[460px] xl:min-h-[500px]">
            <div className="w-full lg:w-1/2 flex flex-col justify-center items-center text-center pt-4 md:pt-8 lg:pt-12 pb-4 md:pb-8 lg:pb-12 px-0 lg:px-8">
              <h1 className="mt-3 md:mt-4 text-5xl md:text-6xl xl:text-6xl font-black text-[#1a1a1a] leading-[1.18] tracking-tight">
                Rubbish{" "}
                <span className="relative inline-block text-[#F64A01]">
                  <span
                    className="inline-block"
                    style={{ transform: "skewX(-9deg)" }}
                  >
                    Gone.
                  </span>
                </span>{" "}
                Without the Hassle.
              </h1>
              <p className="mt-4 md:mt-6 text-lg md:text-xl xl:text-lg text-[#1a1a1a]/75 max-w-[600px] font-medium leading-relaxed">
                Fast rubbish removal across Melbourne. We do the lifting, loading and removal, you just show us what needs to go.
              </p>

              <div className="mt-3 md:mt-5 flex flex-wrap justify-center gap-3 w-full sm:w-auto">
                {trustIndicators.map(({ label, Icon }) => (
                  <div
                    key={label}
                    className="inline-flex items-center gap-2 bg-white/75 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm px-3.5 py-2"
                  >
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#F64A01] ring-2 ring-[#1a1a1a]/10 shrink-0">
                      <Icon className="w-4 h-4 text-white" strokeWidth={2.5} />
                    </span>
                    <span className="text-sm md:text-base font-bold text-[#1a1a1a] whitespace-nowrap">{label}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 md:mt-7 flex flex-col sm:flex-row-reverse gap-4 justify-center w-full sm:w-auto">
                <a
                  href="/#quote"
                  className="inline-flex items-center justify-center gap-2 bg-[#F64A01] text-white px-6 py-3 md:px-8 md:py-4 text-lg font-bold rounded-md shadow-lg shadow-[#F64A01]/25 hover:bg-[#1a1a1a] hover:text-white transition-colors text-center"
                >
                  Get a Free Quote
                  <ArrowRight className="w-5 h-5 shrink-0" />
                </a>
                <a
                  href="/#how-it-works"
                  className="bg-transparent border-2 border-[#1a1a1a] text-[#1a1a1a] px-6 py-3 md:px-8 md:py-4 text-lg font-bold rounded-md hover:bg-[#1a1a1a]/5 transition-colors text-center"
                >
                  See How It Works
                </a>
              </div>

              {/* Google Reviews trust badge, compact on-brand pill.
                  One line: [G] ★★★★★ 5.0 · 27 reviews. Clickable, opens
                  the Google Business Profile (@/data/reviews source). */}
              <div className="mt-4 md:mt-6 flex justify-center w-full">
                <a
                  href={googleBusinessUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="View our Google Reviews"
                  className="group inline-flex items-center gap-1.5 bg-[#1a1a1a] text-[#FDC00E] rounded-full px-4 py-2 cursor-pointer whitespace-nowrap transition-colors hover:ring-2 hover:ring-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange"
                >
                  <GoogleGIcon className="w-4 h-4 shrink-0" />
                  <div className="flex items-center gap-0.5" aria-hidden="true">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 text-[#FDC00E] fill-[#FDC00E]" strokeWidth={0} />
                    ))}
                  </div>
                  <span className="text-sm font-bold tabular-nums leading-none">
                    {googleReviewSummary.rating.toFixed(1)}
                  </span>
                  <span className="text-sm font-medium text-[#FDC00E]/60 leading-none">·</span>
                  <span className="text-sm font-medium text-[#FDC00E]/80 leading-none">
                    {googleReviewSummary.reviewCount} reviews
                  </span>
                </a>
              </div>
            </div>
            <div className="w-full lg:w-1/2 relative flex items-end justify-center lg:justify-end mt-0 lg:mt-0 pb-4 md:pb-0">
              <img
                src={heroTruck}
                srcSet={heroTruckSrcSet}
                sizes="(min-width: 1024px) 55vw, 110vw"
                fetchPriority="high"
                decoding="async"
                loading="eager"
                alt="OZ WASTE CO. black Hilux ute and OZ WASTE trailer loaded with household rubbish, with hi-vis kangaroo worker"
                width="1200"
                height="800"
                className="relative z-10 w-[110%] -mx-[5%] max-w-none sm:w-[105%] sm:-mx-[2.5%] md:w-full md:mx-0 lg:w-[110%] lg:-ml-8 lg:max-w-none lg:max-h-[440px] xl:max-h-[480px] xl:-ml-12 2xl:max-h-[520px] 2xl:-ml-24 h-auto object-contain object-bottom lg:object-right-bottom"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Diagonal divider, black wedge overlapping up into the yellow hero
          so the truck wheels appear to sit right on the black feature strip. */}
      <div ref={dividerRef} className="relative -mt-8 md:-mt-10">
        <svg viewBox="0 0 1440 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto block" preserveAspectRatio="none">
          <polygon points="0,0 1440,32 1440,40 0,40" fill="#111111" />
        </svg>
      </div>
    </>
  );
};

export default HeroSection;
